import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaV1Service } from '../service';
import { FetchCallGroupAnalyticsForMultiTenantIO } from '../types';

export const getFetchCallGroupAnalyticsForMultiTenantQueryKey = (
  req: FetchCallGroupAnalyticsForMultiTenantIO['input']
) => ['V1', 'FetchCallGroupAnalyticsForMultiTenant', req] as const;
type QueryKey = ReturnType<typeof getFetchCallGroupAnalyticsForMultiTenantQueryKey>;

/**
 * A hook that returns a query for the `FetchCallGroupAnalyticsForMultiTenant` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchCallGroupAnalyticsForMultiTenantQuery = <
  E = unknown,
  D = FetchCallGroupAnalyticsForMultiTenantIO['output']
>(
  req: FetchCallGroupAnalyticsForMultiTenantIO['input'],
  options?: LimitedSchemaQueryOptions<FetchCallGroupAnalyticsForMultiTenantIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<FetchCallGroupAnalyticsForMultiTenantIO['output'], E, D, QueryKey>({
    queryKey: getFetchCallGroupAnalyticsForMultiTenantQueryKey(req),
    queryFn: () => SchemaV1Service.FetchCallGroupAnalyticsForMultiTenant(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `FetchCallGroupAnalyticsForMultiTenant` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchCallGroupAnalyticsForMultiTenantQueries = <
  E = unknown,
  D = FetchCallGroupAnalyticsForMultiTenantIO['output']
>(
  argsArr: {
    req: FetchCallGroupAnalyticsForMultiTenantIO['input'];
    options?: LimitedSchemaQueryOptions<FetchCallGroupAnalyticsForMultiTenantIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getFetchCallGroupAnalyticsForMultiTenantQueryKey(req),
      queryFn: () => SchemaV1Service.FetchCallGroupAnalyticsForMultiTenant(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useFetchCallGroupAnalyticsForMultiTenantQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `FetchCallGroupAnalyticsForMultiTenant` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchFetchCallGroupAnalyticsForMultiTenant = () => {
  const queryClient = useQueryClient();

  const fetchFetchCallGroupAnalyticsForMultiTenant = async <
    E = unknown,
    D = FetchCallGroupAnalyticsForMultiTenantIO['output']
  >(
    req: FetchCallGroupAnalyticsForMultiTenantIO['input'],
    options?: LimitedSchemaFetchQueryOptions<FetchCallGroupAnalyticsForMultiTenantIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<FetchCallGroupAnalyticsForMultiTenantIO['output'], E, D, QueryKey>(
      getFetchCallGroupAnalyticsForMultiTenantQueryKey(req),
      () => SchemaV1Service.FetchCallGroupAnalyticsForMultiTenant(req, httpOptions),
      options
    );

    return response;
  };

  return fetchFetchCallGroupAnalyticsForMultiTenant;
};
